/**
 * Notification Targets — admin tool to manage reusable outbound webhooks.
 *
 * A target is a named HTTP endpoint (URL + method + headers + body template)
 * with a vault-encrypted secret. It plays two roles in Scheduled Jobs: the
 * "webhook" job kind POSTs it directly, and any job can fire it as a notify hook
 * (on success / failure / always).
 *
 * Template placeholders substituted into the URL, every header value, and body:
 *   {{title}} {{message}} {{status}} {{secret}}          — raw
 *   {{title_json}} {{message_json}}                       — escaped for a JSON string
 *   {{title_url}}  {{message_url}}                        — query-escaped (form bodies)
 * {{secret}} is the decrypted vaulted value, so a Slack/Discord webhook path or a
 * Pushover token pair never sits on disk in plaintext.
 */
import { createSignal, createResource, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { toast } from '../lib/toast.js';
import {
  listTargets, createTarget, updateTarget, deleteTarget, testTarget,
} from '../lib/notificationtargets.js';

const blankInput = () => ({
  name: '', url: '', method: 'POST', contentType: 'application/json',
  headers: '', bodyTemplate: '', secret: '', kind: 'custom', preset: {},
});

// Quick-fill presets for the simple webhook services (no extra fields). Each
// fills the form; the admin pastes the actual token(s) into Secret.
const PRESETS = [
  {
    id: 'slack', label: 'Slack',
    fill: {
      url: 'https://hooks.slack.com/services/{{secret}}', method: 'POST',
      contentType: 'application/json',
      bodyTemplate: '{"text": "*{{title_json}}*\\n{{message_json}}"}',
      secretHint: 'T0000/B0000/xxxxxxxx  (the /services/… path)',
    },
  },
  {
    id: 'discord', label: 'Discord',
    fill: {
      url: 'https://discord.com/api/webhooks/{{secret}}', method: 'POST',
      contentType: 'application/json',
      bodyTemplate: '{"content": "**{{title_json}}**\\n{{message_json}}"}',
      secretHint: '123456789/xxxxxxxx  (the webhooks/… id/token)',
    },
  },
];

// Pushover built-in sounds + priorities — the friendly fields the preset exposes.
const PUSHOVER_SOUNDS = ['', 'pushover', 'bike', 'bugle', 'cashregister', 'classical',
  'cosmic', 'falling', 'gamelan', 'incoming', 'intermission', 'magic', 'mechanical',
  'pianobar', 'siren', 'spacealarm', 'tugboat', 'alien', 'climb', 'persistent',
  'echo', 'updown', 'vibrate', 'none'];
const PUSHOVER_PRIORITIES = [
  { v: -2, l: 'Lowest — no notification/sound' },
  { v: -1, l: 'Low — quiet' },
  { v: 0, l: 'Normal' },
  { v: 1, l: 'High — bypass quiet hours' },
  { v: 2, l: 'Emergency — require acknowledgement' },
];
const defaultPO = () => ({ sound: '', priority: 0, device: '' });

// Generate the actual target fields from the Pushover options. The send path is
// unchanged — it just renders this form-encoded body. The user still supplies
// token=APP_TOKEN&user=USER_KEY as the (vaulted) secret.
function buildPushover(po) {
  let body = '{{secret}}&title={{title_url}}&message={{message_url}}&priority=' + Number(po.priority || 0);
  if (po.sound) body += '&sound=' + encodeURIComponent(po.sound);
  if (po.device) body += '&device=' + encodeURIComponent(po.device.trim());
  if (Number(po.priority) === 2) body += '&retry=60&expire=3600'; // emergency needs these
  return {
    url: 'https://api.pushover.net/1/messages.json', method: 'POST',
    contentType: 'application/x-www-form-urlencoded', bodyTemplate: body,
  };
}

export default function NotificationTargets(props) {
  const [list, { refetch }] = createResource(listTargets);
  const [editing, setEditing] = createSignal(null); // null | { id?, input, isNew, secretHint? }
  const [busyId, setBusyId] = createSignal(0);

  const openNew = () => setEditing({ input: blankInput(), isNew: true, po: defaultPO() });
  const openEdit = (t) => {
    const po = t.kind === 'pushover' ? { ...defaultPO(), ...(t.preset || {}) } : defaultPO();
    setEditing({
      id: t.id, isNew: false, po,
      // Secret stays blank — empty (= null) means "leave unchanged" on update.
      input: {
        name: t.name, url: t.url, method: t.method, contentType: t.contentType,
        headers: t.headers || '', bodyTemplate: t.bodyTemplate || '', secret: '',
        kind: t.kind || 'custom', preset: t.preset || {},
      },
    });
  };

  const setField = (k, v) => setEditing({ ...editing(), input: { ...editing().input, [k]: v } });

  // Switch to the Pushover preset (friendly fields). Generates the template now.
  const usePushover = () => {
    const po = defaultPO();
    const e = editing();
    setEditing({ ...e, secretHint: 'token=APP_TOKEN&user=USER_KEY',
      input: { ...e.input, kind: 'pushover', preset: po, ...buildPushover(po) } });
  };
  // Edit a Pushover field → regenerate the template + remember the field values.
  const setPushover = (field, val) => {
    const e = editing();
    const po = { ...e.po, [field]: val };
    setEditing({ ...e, po, input: { ...e.input, kind: 'pushover', preset: po, ...buildPushover(po) } });
  };
  // A simple webhook quick-fill (Slack/Discord) or back to raw Custom.
  const applyPreset = (p) => {
    const e = editing();
    setEditing({
      ...e, secretHint: p.fill.secretHint,
      input: {
        ...e.input, kind: 'custom', preset: {},
        url: p.fill.url, method: p.fill.method, contentType: p.fill.contentType,
        bodyTemplate: p.fill.bodyTemplate,
      },
    });
  };
  const useCustom = () => setField('kind', 'custom');

  const save = async () => {
    const e = editing();
    if (!e) return;
    const i = e.input;
    const payload = {
      name: i.name.trim(), url: i.url.trim(), method: i.method,
      contentType: i.contentType.trim(), headers: i.headers.trim(),
      bodyTemplate: i.bodyTemplate, kind: i.kind || 'custom', preset: i.preset || {},
      // Edit + blank secret → null ("don't change"); new → '' (no secret).
      secret: i.secret ? i.secret : (e.isNew ? '' : null),
    };
    try {
      if (e.isNew) await createTarget(payload);
      else await updateTarget(e.id, payload);
      toast.success('Notification target saved.');
      setEditing(null);
      refetch();
    } catch (err) { toast.error('Save failed: ' + err.message); }
  };

  const remove = async (t) => {
    if (!confirm(`Delete notification target "${t.name}"?`)) return;
    setBusyId(t.id);
    try { await deleteTarget(t.id); toast.success('Target deleted.'); refetch(); }
    catch (err) { toast.error('Delete failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const test = async (t) => {
    setBusyId(t.id);
    try {
      const r = await testTarget(t.id);
      if (r.ok) toast.success(`"${t.name}" responded HTTP ${r.statusCode}.`);
      else toast.error('Test failed: ' + (r.error || 'unknown error'));
    } catch (err) { toast.error('Test failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  return (
    <Modal open={true} onClose={props.onClose} title="Notification Targets" wide>
      <p class="muted" style="font-size:12px;margin:0 0 12px;">
        Reusable outbound webhooks (Pushover, Slack, Discord, any HTTP). Scheduled
        jobs POST them directly or fire them as a notify hook. Tokens are encrypted
        at rest with the master key and rendered into the request via <code class="mono">{'{{secret}}'}</code>.
      </p>

      <div style="display:flex;justify-content:flex-end;margin:0 0 10px;">
        <button class="btn" onClick={openNew}>+ New target</button>
      </div>

      <Show when={!list.loading} fallback={<div class="muted">Loading…</div>}>
        <Show when={(list() || []).length === 0}>
          <div class="muted" style="font-size:12px;">No targets yet — create one to send job notifications.</div>
        </Show>
        <table class="settings-backups mono" style="width:100%;">
          <thead><tr><th>Name</th><th>Method</th><th>URL</th><th>Secret</th><th></th></tr></thead>
          <tbody>
            <For each={list() || []}>
              {(t) => (
                <tr>
                  <td>{t.name}</td>
                  <td>{t.method}</td>
                  <td title={t.url} style="max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{t.url}</td>
                  <td>{t.hasSecret ? '✓ vaulted' : <span class="muted">none</span>}</td>
                  <td style="text-align:right;white-space:nowrap;">
                    <button class="btn btn-sm" disabled={busyId() === t.id} onClick={() => test(t)}>Test</button>
                    <button class="btn btn-sm btn-ghost" onClick={() => openEdit(t)}>Edit</button>
                    <button class="btn btn-sm btn-danger" disabled={busyId() === t.id} onClick={() => remove(t)}>Delete</button>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>

      <Show when={editing()}>
        <Modal open={true} onClose={() => setEditing(null)} title={editing().isNew ? 'New notification target' : 'Edit target'} wide>
          <div class="settings-row"><label>Preset</label>
            <div style="display:flex;gap:6px;flex-wrap:wrap;">
              <button class="btn btn-sm btn-ghost" classList={{ 'btn-active': editing().input.kind === 'pushover' }} onClick={usePushover}>Pushover</button>
              <For each={PRESETS}>
                {(p) => <button class="btn btn-sm btn-ghost" onClick={() => applyPreset(p)}>{p.label}</button>}
              </For>
              <button class="btn btn-sm btn-ghost" classList={{ 'btn-active': editing().input.kind !== 'pushover' }} onClick={useCustom}>Custom (raw)</button>
            </div>
          </div>

          <div class="settings-row"><label>Name</label>
            <input class="input" value={editing().input.name} onInput={(e) => setField('name', e.target.value)} /></div>

          {/* Pushover preset → friendly fields; the URL/body are generated. */}
          <Show when={editing().input.kind === 'pushover'}>
            <div class="settings-row"><label>Sound</label>
              <select class="input" value={editing().po.sound} onChange={(e) => setPushover('sound', e.target.value)}>
                <For each={PUSHOVER_SOUNDS}>{(s) => <option value={s}>{s === '' ? '(device default)' : s}</option>}</For>
              </select></div>
            <div class="settings-row"><label>Priority</label>
              <select class="input" value={editing().po.priority} onChange={(e) => setPushover('priority', Number(e.target.value))}>
                <For each={PUSHOVER_PRIORITIES}>{(p) => <option value={p.v}>{p.l}</option>}</For>
              </select></div>
            <div class="settings-row"><label>Device <span class="muted" style="font-weight:400;">(optional)</span></label>
              <input class="input mono" placeholder="all devices" value={editing().po.device}
                     onInput={(e) => setPushover('device', e.target.value)} /></div>
            <p class="muted" style="font-size:11px;margin:4px 0 0;">
              The request is built for you. Put your Pushover credentials in <strong>Secret</strong> as
              {' '}<code class="mono">token=APP_TOKEN&amp;user=USER_KEY</code>. Switch to <em>Custom (raw)</em> to hand-edit the request.
            </p>
          </Show>

          {/* Custom / webhook presets → raw request fields. */}
          <Show when={editing().input.kind !== 'pushover'}>
            <div class="settings-row"><label>Method</label>
              <select class="input" value={editing().input.method} onChange={(e) => setField('method', e.target.value)}>
                <option value="POST">POST</option><option value="PUT">PUT</option><option value="GET">GET</option>
              </select></div>
            <div class="settings-row"><label>URL</label>
              <input class="input mono" value={editing().input.url} placeholder="https://…  (may contain {{secret}})"
                     onInput={(e) => setField('url', e.target.value)} /></div>
            <div class="settings-row"><label>Content-Type</label>
              <input class="input mono" value={editing().input.contentType}
                     onInput={(e) => setField('contentType', e.target.value)} /></div>
            <div class="settings-row" style="align-items:flex-start;"><label>Headers (JSON)</label>
              <textarea class="input mono" rows="2" placeholder='{"Authorization": "Bearer {{secret}}"}'
                        style="font-size:11.5px;" value={editing().input.headers}
                        onInput={(e) => setField('headers', e.target.value)}></textarea></div>
            <div class="settings-row" style="align-items:flex-start;"><label>Body template</label>
              <textarea class="input mono" rows="3" style="font-size:11.5px;"
                        placeholder='{"text": "{{title_json}}: {{message_json}}"}'
                        value={editing().input.bodyTemplate}
                        onInput={(e) => setField('bodyTemplate', e.target.value)}></textarea></div>
          </Show>

          <div class="settings-row"><label>Secret</label>
            <input class="input mono" type="password"
                   placeholder={editing().secretHint || (editing().input.kind === 'pushover' ? 'token=APP_TOKEN&user=USER_KEY' : (editing().isNew ? 'token / webhook path (vaulted)' : 'Leave blank to keep current'))}
                   value={editing().input.secret} onInput={(e) => setField('secret', e.target.value)} /></div>
          <p class="muted" style="font-size:11px;margin:4px 0 0;">
            Placeholders: <code class="mono">{'{{title}} {{message}} {{status}} {{secret}}'}</code>,
            and <code class="mono">{'{{*_json}}'}</code> / <code class="mono">{'{{*_url}}'}</code> for JSON / form bodies.
          </p>

          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button class="btn" onClick={save}>{editing().isNew ? 'Create' : 'Save'}</button>
          </div>
        </Modal>
      </Show>
    </Modal>
  );
}
